/**
 * Mappträd för dokumentdestinationer (SharePoint/OneDrive).
 *
 * Varje projekt kan få en färdig mappstruktur skapad i kundens eget system så att
 * hela förloppet går att följa där dokumenten faktiskt lagras. Strukturen speglar
 * faserna i Bas-processen (samma indelning som ZIP-exporten).
 */

export type FolderLayoutKey = "faser" | "roller" | "enkel";

export interface FolderNode {
  /** Relativ sökväg under destinationens rotmapp, t.ex. "01 Planering/Risk". */
  path: string;
  /** Kort förklaring som visas i förhandsvisningen. */
  hint?: string;
}

export interface FolderLayout {
  key: FolderLayoutKey;
  title: string;
  description: string;
  folders: FolderNode[];
  /** Mappar ett dokumentnummer (t.ex. "12") till en relativ undermapp. */
  resolve: (docNumber: string | null | undefined, documentType?: string | null) => string;
}

const docNum = (docNumber: string | null | undefined): number | null => {
  if (!docNumber) return null;
  const n = parseInt(String(docNumber).replace(/\D/g, ""), 10);
  return Number.isFinite(n) ? n : null;
};

/** Fas per dokumentnummer – samma logik som ZIP-exportens faser. */
export const phaseForDoc = (docNumber: string | null | undefined): 1 | 2 | 3 | 4 => {
  const n = docNum(docNumber);
  if (n === null) return 1;
  if (n <= 9) return 1; // Dok 00–09 Planering
  if (n <= 19) return 2; // Dok 10–19 Projektering
  if (n <= 29) return 3; // Dok 20–29 Utförande
  return 4; // Dok 30+ Bruksskede
};

const PHASE_FOLDERS = [
  "01 Planering",
  "02 Projektering",
  "03 Utförande",
  "04 Bruksskede",
] as const;

export const FOLDER_LAYOUTS: Record<FolderLayoutKey, FolderLayout> = {
  faser: {
    key: "faser",
    title: "Efter skede (rekommenderas)",
    description:
      "Mappar i samma ordning som Bas-processen: Planering, Projektering, Utförande och Bruksskede. Enklast att följa hela förloppet.",
    folders: [
      { path: "00 Arbetsmiljöplan", hint: "AMP – huvuddokumentet (Dok 12)" },
      { path: "01 Planering", hint: "Dok 00–09: SAM-rutin, förhandsanmälan, tidplan" },
      { path: "01 Planering/Underlag", hint: "Inkommet underlag från byggherre" },
      { path: "02 Projektering", hint: "Dok 10–19: arbetsområde, SKR, risksammanställning" },
      { path: "02 Projektering/Projektörer", hint: "Projektörernas redovisning av kvarstående risker" },
      { path: "03 Utförande", hint: "Dok 20–29: arbetsberedningar, skyddsronder, tillbud" },
      { path: "03 Utförande/Arbetsberedningar" },
      { path: "03 Utförande/Skyddsronder" },
      { path: "03 Utförande/Tillbud och olyckor" },
      { path: "04 Bruksskede", hint: "Dok 30+: bruksrisker, överlämning, slutdokumentation" },
      { path: "04 Bruksskede/Överlämning" },
      { path: "99 Arkiv", hint: "Tidigare versioner" },
    ],
    resolve: (docNumber, documentType) => {
      const type = (documentType ?? "").toLowerCase();
      if (docNum(docNumber) === 12) return "00 Arbetsmiljöplan";
      if (type.includes("arbetsberedning")) return "03 Utförande/Arbetsberedningar";
      if (type.includes("skyddsrond")) return "03 Utförande/Skyddsronder";
      if (type.includes("tillbud") || type.includes("incident")) return "03 Utförande/Tillbud och olyckor";
      return PHASE_FOLDERS[phaseForDoc(docNumber) - 1];
    },
  },
  roller: {
    key: "roller",
    title: "Efter roll",
    description:
      "Mappar per ansvarig roll: Byggherre, Bas-P, Projektör, Bas-U och Entreprenör. Bra när flera parter arbetar i samma bibliotek.",
    folders: [
      { path: "00 Arbetsmiljöplan", hint: "AMP – huvuddokumentet" },
      { path: "01 Byggherre", hint: "Dok 00–09" },
      { path: "02 Bas-P", hint: "Dok 10–19" },
      { path: "03 Projektör", hint: "Kvarstående risker och redovisning" },
      { path: "04 Bas-U", hint: "Dok 20–29" },
      { path: "05 Entreprenör", hint: "Arbetsberedningar och kompetensbevis" },
      { path: "06 Bruksskede", hint: "Dok 30+" },
      { path: "99 Arkiv" },
    ],
    resolve: (docNumber, documentType) => {
      const type = (documentType ?? "").toLowerCase();
      if (docNum(docNumber) === 12) return "00 Arbetsmiljöplan";
      if (type.includes("arbetsberedning")) return "05 Entreprenör";
      if (type.includes("projektör") || type.includes("designer")) return "03 Projektör";
      const phase = phaseForDoc(docNumber);
      if (phase === 1) return "01 Byggherre";
      if (phase === 2) return "02 Bas-P";
      if (phase === 3) return "04 Bas-U";
      return "06 Bruksskede";
    },
  },
  enkel: {
    key: "enkel",
    title: "Enkel",
    description: "Endast tre mappar: Arbetsmiljöplan, Dokument och Arkiv. För mindre projekt.",
    folders: [
      { path: "Arbetsmiljöplan" },
      { path: "Dokument" },
      { path: "Arkiv" },
    ],
    resolve: (docNumber) => (docNum(docNumber) === 12 ? "Arbetsmiljöplan" : "Dokument"),
  },
};

export const DEFAULT_LAYOUT: FolderLayoutKey = "faser";

export const getLayout = (key: string | null | undefined): FolderLayout =>
  FOLDER_LAYOUTS[(key as FolderLayoutKey) ?? DEFAULT_LAYOUT] ?? FOLDER_LAYOUTS[DEFAULT_LAYOUT];

/** Undermapp för ett dokument enligt destinationens valda struktur. */
export const subPathForDocument = (
  layoutKey: string | null | undefined,
  docNumber: string | null | undefined,
  documentType?: string | null
): string => getLayout(layoutKey).resolve(docNumber, documentType);

/** Rensar ett projektnamn så det fungerar som mappnamn i SharePoint/OneDrive. */
export const safeFolderName = (name: string): string =>
  name
    .replace(/[\\/:*?"<>|#%]/g, "-")
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, 100) || "Projekt";
